import React, { useState, FunctionComponent } from 'react';
import { Dropdown, Icon, Popup } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import { FeatureFlag } from '../../modules/featureflags';
import { CustomIcon } from '../icons';
import type { Session } from '../../modules/session';
import { WriteOnly } from '../common/grid/WriteOnly';
import { CONTEXT_MENU_KEYS } from '../../constants/contextMenuEntries';
import { SystemOnlyIcon, SystemOnlyPopup } from '../common/SystemOnlyBadge';

interface SidebarUserProfileProps {
  firstName: string;
  currentUserId: string;
  impersonated: boolean;
  session: Session;
  onLogOut: () => void;
  onDeleteImpersonation: () => void;
  isMinimized?: boolean;
}

const SidebarUserProfile: FunctionComponent<SidebarUserProfileProps> = ({
  firstName,
  currentUserId,
  impersonated,
  session,
  onLogOut,
  onDeleteImpersonation,
  isMinimized = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const showWebhooksMenuOption = session.isSystemAdmin || session.isOrganizationAdmin;
  const showSystemSettings = session.isSystemAdmin; // SysAdmin only
  const appVersion = import.meta.env.VITE_APP_VERSION;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`sidebar-user-profile ${isMinimized ? 'minimized' : ''}`}>
      {isMinimized ? (
        // Minimized version - just show user icon with popup menu
        <Dropdown
          trigger={
            <div className="sidebar-user-profile-trigger minimized">
                <div className="sidebar-user-avatar">
                  <CustomIcon name="user-circle-01" size={24} />
                </div>
            </div>
          }
          icon={null}
          floating
          upward
        >
          <Dropdown.Menu>
            <WriteOnly>
              <Dropdown.Item as={Link} to={CONTEXT_MENU_KEYS.ACCOUNT.EDIT_PAGE.getUrl({ id: currentUserId }, {})}>
                <CustomIcon name="settings-01" size={14} style={{ marginRight: '8px' }} />
                {I18n.t('general.settings')}
              </Dropdown.Item>
            </WriteOnly>

            <FeatureFlag feature="exportData">
              <Dropdown.Item as={Link} to={CONTEXT_MENU_KEYS.GENERAL.EXPORT_HISTORY.getUrl({}, {})}>
                <CustomIcon name="download-01" size={14} style={{ marginRight: '8px' }} />
                {I18n.t('general.export-history')}
              </Dropdown.Item>
            </FeatureFlag>

            <Dropdown.Item as="a" href={CONTEXT_MENU_KEYS.GENERAL.SUPPORT_URL.getUrl({}, {})} target={CONTEXT_MENU_KEYS.GENERAL.SUPPORT_URL.target} rel="noopener noreferrer">
              <CustomIcon name="life-buoy-01" size={14} style={{ marginRight: '8px' }} />
              {I18n.t('general.support')}
            </Dropdown.Item>

            {showWebhooksMenuOption && (
              <Dropdown.Item as={Link} to={CONTEXT_MENU_KEYS.GENERAL.WEBHOOKS.getUrl({}, {})}>
                <CustomIcon name="code-01" size={14} style={{ marginRight: '8px' }} />
                {I18n.t('general.webhooks')}
              </Dropdown.Item>
            )}
            {showSystemSettings && (
              <SystemOnlyPopup>
                <Dropdown.Item as={Link} to={CONTEXT_MENU_KEYS.GENERAL.SYSTEM_SETTINGS.getUrl({}, {})}>
                  <CustomIcon name="settings-02" size={14} style={{ marginRight: '8px' }} />
                  {I18n.t('general.system-settings')}
                  <SystemOnlyIcon />
                </Dropdown.Item>
              </SystemOnlyPopup>
            )}
            {impersonated && (
              <Dropdown.Item onClick={onDeleteImpersonation}>
                <CustomIcon name="user-02" size={14} style={{ marginRight: '8px' }} />
                {I18n.t('account.remove-impersonate-action')}
              </Dropdown.Item>
            )}

            <Dropdown.Item onClick={onLogOut}>
              <CustomIcon name="logout-01" size={14} style={{ marginRight: '8px' }} />
              {I18n.t('general.log-out')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        // Expanded version - show full accordion
        <>
          <div className="sidebar-user-profile-trigger" onClick={toggleExpanded}>
            <div className="sidebar-user-avatar">
              <CustomIcon name="user-circle-01" size={24} />
            </div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">
                {I18n.t('general.greeting', { name: firstName })}
              </span>
              <Icon
                name={isExpanded ? 'chevron down' : 'chevron up'}
                className="sidebar-user-chevron"
              />
            </div>
          </div>

          <div className={`sidebar-user-accordion ${isExpanded ? 'expanded' : ''}`}>
            <div className="sidebar-user-accordion-content">
              <WriteOnly>
                <Link
                  to={CONTEXT_MENU_KEYS.ACCOUNT.EDIT_PAGE.getUrl({ id: currentUserId }, {})}
                  className="sidebar-user-accordion-item"
                  onClick={() => setIsExpanded(false)}
                >
                  <CustomIcon name="settings-01" size={16} />
                  {I18n.t('general.settings')}
                </Link>
              </WriteOnly>

              <FeatureFlag feature="exportData">
                <Link
                  to={CONTEXT_MENU_KEYS.GENERAL.EXPORT_HISTORY.getUrl({}, {})}
                  className="sidebar-user-accordion-item"
                  onClick={() => setIsExpanded(false)}
                >
                  <CustomIcon name="download-01" size={16} />
                  {I18n.t('general.export-history')}
                </Link>
              </FeatureFlag>

              <a
                href={CONTEXT_MENU_KEYS.GENERAL.SUPPORT_URL.getUrl({}, {})}
                target={CONTEXT_MENU_KEYS.GENERAL.SUPPORT_URL.target}
                rel="noopener noreferrer"
                className="sidebar-user-accordion-item"
                onClick={() => setIsExpanded(false)}
              >
                <CustomIcon name="life-buoy-01" size={16} />
                {I18n.t('general.support')}
              </a>

              {showWebhooksMenuOption && (
                <Link
                  to={CONTEXT_MENU_KEYS.GENERAL.WEBHOOKS.getUrl({}, {})}
                  className="sidebar-user-accordion-item"
                  onClick={() => setIsExpanded(false)}
                >
                  <CustomIcon name="code-01" size={16} />
                  {I18n.t('general.webhooks')}
                </Link>
              )}
              {showSystemSettings && (
                <SystemOnlyPopup>
                  <Link
                    to={CONTEXT_MENU_KEYS.GENERAL.SYSTEM_SETTINGS.getUrl({}, {})}
                    className="sidebar-user-accordion-item"
                    onClick={() => setIsExpanded(false)}
                  >
                    <CustomIcon name="settings-02" size={16} />
                    {I18n.t('general.system-settings')}
                    <SystemOnlyIcon />
                  </Link>
                </SystemOnlyPopup>
              )}

              {
                impersonated && (
                  <Link
                    to="#"
                    className="sidebar-user-accordion-item"
                    onClick={(e) => {
                      e.preventDefault();
                      onDeleteImpersonation();
                      setIsExpanded(false);
                    }}
                  >
                    <CustomIcon name="user-02" size={16} />
                    {I18n.t('account.remove-impersonate-action')}
                  </Link>
                )
              }

              <Link
                to="#"
                className="sidebar-user-accordion-item logout"
                onClick={(e) => {
                  e.preventDefault();
                  onLogOut();
                  setIsExpanded(false);
                }}
              >
                <CustomIcon name="logout-01" size={16} />
                {I18n.t('general.log-out')}
              </Link>
            </div >
          </div >
        </>
      )}
    </div >
  );
};

export default SidebarUserProfile;
